import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Field,
  Input,
  Heading,
  VStack,
  Text,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { toast } from '../utils/toast';

const ChangePasswordRequired: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({ title: 'Error', description: 'Please fill in all fields.', status: 'error' });
      return;
    }
    if (newPassword.length < 8) {
      toast({ title: 'Error', description: 'New password must be at least 8 characters.', status: 'error' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: 'Error', description: 'New password and confirmation do not match.', status: 'error' });
      return;
    }
    setLoading(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      updateUser({ mustChangePassword: false });
      toast({ title: 'Success', description: 'Password changed. You can now use the application.', status: 'success' });
      navigate('/dashboard');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Password change failed.',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
      px={4}
    >
      <Container maxW="md">
        <Box bg="white" p={10} borderRadius="2xl" boxShadow="2xl">
          <VStack gap={6} align="stretch">
            <Box textAlign="center" mb={2}>
              <Heading size="xl" mb={2} bgGradient="to-r" gradientFrom="purple.600" gradientTo="blue.600" style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Password change required
              </Heading>
              <Text color="gray.600" fontSize="sm">
                You must set a new password on first login (min. 8 characters).
              </Text>
            </Box>

            <form onSubmit={handleSubmit}>
              <VStack gap={5}>
                <Field.Root required>
                  <Field.Label>Current password</Field.Label>
                  <Input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Password you received"
                    size="lg"
                    borderRadius="lg"
                  />
                </Field.Root>
                <Field.Root required>
                  <Field.Label>New password (min. 8 characters)</Field.Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password"
                    size="lg"
                    borderRadius="lg"
                    minLength={8}
                  />
                </Field.Root>
                <Field.Root required>
                  <Field.Label>Confirm new password</Field.Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat new password"
                    size="lg"
                    borderRadius="lg"
                  />
                </Field.Root>
                <Button
                  type="submit"
                  width="100%"
                  size="lg"
                  loading={loading}
                  bgGradient="to-r"
                  gradientFrom="purple.600"
                  gradientTo="blue.600"
                  color="white"
                  borderRadius="lg"
                  _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                >
                  Save password and continue
                </Button>
              </VStack>
            </form>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default ChangePasswordRequired;
